import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { finalize } from 'rxjs/operators';
import { ApiService } from 'src/app/core/providers/api/api.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

interface CommentMention {
  id: string;
  commentContent: string;
  senderFullName: string;
  commentDate: string;
  companyName: string | null;
  companyId: string | null;
  isRead: boolean;
}

interface ProcessedCommentMention {
  id: string;
  content: string;
  senderFullName: string;
  firstName: string;
  lastName: string;
  authorInitial: string;
  commentDate: Date;
  timeAgo: string;
  companyName: string | null;
  companyId: string | null;
  isRead: boolean;
}

@Component({
  selector: 'app-comment-widget',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './comment-widget.component.html',
  styleUrl: './comment-widget.component.scss',
})
export class CommentWidgetComponent implements OnInit {
  @Input() widgetData!: any;

  comments: ProcessedCommentMention[] = [];
  isLoading: boolean = true;

  constructor(
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private router: Router,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadComments();
  }

  loadComments(): void {
    this.isLoading = true;

    // Utiliser l'API comment_mentions qui retourne directement les commentaires mentionnant l'utilisateur connecté
    this.apiService
      .getAll('comment_mentions', 1, 100, {
        'order[createdAt]': 'DESC',
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: any) => {
          const commentMentions: CommentMention[] = response.member || response['hydra:member'] || [];
          this.comments = this.processCommentMentions(commentMentions);
        },
        error: (err) => {
          console.error('Erreur lors du chargement des commentaires', err);
          this.isLoading = false;
        },
      });
  }

  private processCommentMentions(mentions: CommentMention[]): ProcessedCommentMention[] {
    return mentions.map((mention) => {
      // Parser senderFullName pour extraire firstName et lastName
      const nameParts = mention.senderFullName?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      return {
        id: mention.id,
        content: mention.commentContent,
        senderFullName: mention.senderFullName,
        firstName,
        lastName,
        authorInitial: this.getInitial(firstName, lastName),
        commentDate: new Date(mention.commentDate),
        timeAgo: this.getTimeAgo(new Date(mention.commentDate)),
        companyName: mention.companyName,
        companyId: mention.companyId,
        isRead: mention.isRead,
      };
    });
  }

  getTimeAgo(date: Date): string {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    if (seconds < 60) return this.translate.instant('commentwidget.timeAgo.justNow');
    if (seconds < 120) return this.translate.instant('commentwidget.timeAgo.oneMinute');
    if (seconds < 3600) return this.translate.instant('commentwidget.timeAgo.minutes', { count: Math.floor(seconds / 60) });
    if (seconds < 7200) return this.translate.instant('commentwidget.timeAgo.oneHour');
    if (seconds < 86400) return this.translate.instant('commentwidget.timeAgo.hours', { count: Math.floor(seconds / 3600) });

    const days = Math.floor(seconds / 86400);
    if (days === 1) return this.translate.instant('commentwidget.timeAgo.yesterday');
    if (days < 7) return this.translate.instant('commentwidget.timeAgo.days', { count: days });

    return date.toLocaleDateString(this.translate.currentLang || 'fr-FR', { day: 'numeric', month: 'long' });
  }

  getInitial(firstName: string, lastName: string): string {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  goToFolder(companyId: string | null): void {
    if (companyId) {
      this.router.navigate(['my-folders/view-folder/', companyId]);
    }
  }
}

